import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AlertCircle, KeyRound, Loader2 } from "lucide-react";
import { memberApi } from "../api/member";
import { API_BASE_URL } from "../api/config";
import { pageRoutes } from "../utils";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [status, setStatus] = useState(token ? "idle" : "invalid");
  const [message, setMessage] = useState("");
  const [form, setForm] = useState({
    newPassword: "",
    confirmPassword: "",
  });

  useEffect(() => {
    let cancelled = false;
    async function validate() {
      if (!token) {
        return;
      }
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/auth/reset-password/validate?token=${encodeURIComponent(token)}`,
          { credentials: "include" }
        );
        const payload = await response.json().catch(() => ({}));
        if (!cancelled && !payload.valid) {
          setStatus("invalid");
          setMessage("This reset link is invalid or expired. Please request a new one.");
        }
      } catch {
        if (!cancelled) {
          setStatus("invalid");
          setMessage("This reset link is invalid or expired. Please request a new one.");
        }
      }
    }
    validate();
    return () => {
      cancelled = true;
    };
  }, [token]);

  function update(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setStatus("loading");
    setMessage("");
    try {
      const payload = await memberApi("/api/auth/reset-password", {
        method: "POST",
        body: JSON.stringify({ token, ...form }),
      });
      setStatus("success");
      setMessage(payload.message || "Password updated. You can now log in.");
      setForm({ newPassword: "", confirmPassword: "" });
    } catch (error) {
      setStatus("error");
      setMessage(
        error instanceof Error
          ? error.message
          : "This reset link is invalid or expired. Please request a new one."
      );
    }
  }

  return (
    <div className="min-h-screen bg-[#FDF8F3] px-4 py-16">
      <div className="mx-auto max-w-md rounded-3xl border border-[#C9A227]/20 bg-white p-8 shadow-sm">
        <div className="flex items-center gap-3">
          <KeyRound className="h-7 w-7 text-[#722F37]" />
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-[#A88520]">
              Password reset
            </p>
            <h1 className="font-serif-elegant text-3xl font-bold text-[#722F37]">
              Choose a new password
            </h1>
          </div>
        </div>

        {message && (
          <div
            className={`mt-6 rounded-2xl border px-4 py-3 text-sm ${
              status === "success"
                ? "border-green-200 bg-green-50 text-green-900"
                : "border-red-200 bg-red-50 text-red-900"
            }`}
          >
            <div className="flex gap-3">
              {status !== "success" && (
                <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              )}
              <span>{message}</span>
            </div>
          </div>
        )}

        {status !== "success" && status !== "invalid" && (
          <form onSubmit={handleSubmit} className="mt-8 space-y-5">
            <PasswordField
              label="New password"
              value={form.newPassword}
              onChange={(value) => update("newPassword", value)}
            />
            <PasswordField
              label="Confirm new password"
              value={form.confirmPassword}
              onChange={(value) => update("confirmPassword", value)}
            />
            <button
              type="submit"
              disabled={status === "loading"}
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#722F37] px-5 py-3 font-semibold text-white hover:bg-[#5A252C] disabled:opacity-70"
            >
              {status === "loading" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <KeyRound className="h-4 w-4" />
              )}
              Update password
            </button>
          </form>
        )}

        <div className="mt-6 flex flex-wrap justify-center gap-4 text-sm">
          <Link className="font-semibold text-[#722F37] underline" to={pageRoutes.ForgotPassword}>
            Request new link
          </Link>
          <Link className="font-semibold text-[#722F37] underline" to={pageRoutes.MemberLogin}>
            Member login
          </Link>
          <Link className="font-semibold text-[#722F37] underline" to={pageRoutes.AdminLogin}>
            Admin login
          </Link>
        </div>
      </div>
    </div>
  );
}

function PasswordField({ label, value, onChange }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-medium text-gray-700">
        {label}
      </span>
      <input
        type="password"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
        autoComplete="new-password"
        required
      />
    </label>
  );
}
